import { Injectable } from '@angular/core';
import { Observable, from, of } from 'rxjs';
import { HttpService } from '@ecp-caf/caf-common';
import { translate } from './i18n/index';
import { ForecastProcessPayload } from '../entity/forecast-process-payload';
import { map } from 'rxjs/operators';
import { ActivityInstanceSlim } from '../flow-chart/entity';

@Injectable()
export class UiFlowchartService {
  constructor(private httpSvc: HttpService) { }

  getForecastProcessListByPayload(payload: ForecastProcessPayload): Observable<any> {
    const url = '/api/runtime/wf/v1.0/processInstances/startProcess-simulation';
    return this.httpSvc.post(url, payload).pipe(
      map((re: any) => re.procDefs)
    );
  }

  getRuntimeProcInstsByDataId(dataId: string): Observable<any> {
    if (dataId) {
      const url = `/api/runtime/wf/v1.0/processInstances/runtimeProcInstList?dataId=${dataId}`;
      return this.httpSvc.get(url);
    } else {
      return from(new Array());
    }
  }

  getHistoricProcInstsByDataId(dataId: string): Observable<any> {
    if (dataId) {
      const url = `/api/runtime/wf/v1.0/processInstances/historicProcInstList?dataId=${dataId}`;
      return this.httpSvc.get(url);
    } else {
      return from(new Array());
    }
  }

  /**
   * 基于流程实例ID获取流程实例
   * @param procInstId 流程实例id
   */
  getProcInstanceById(procInstId: string): Observable<any> {
    const url = `/api/runtime/wf/v1.0/processInstances/${procInstId}`;
    return this.httpSvc.get(url);
  }
  /**
   * 基于流程实例ID获取bpmnModel
   */
  getBpmnModelbyProcInstId(procInstId: string): Observable<any> {
    const url = `/api/runtime/wf/v1.0/processInstances/${procInstId}/bpmnModel`;
    return this.httpSvc.get(url);
  }

  /**
   * 基于流程定义ID获取bpmnModel
   */
  getBpmnModelbyProcDefId(procDefId: string): Observable<any> {
    const url = `/api/runtime/wf/v1.0/procDefs/${procDefId}/bpmnModel`;
    return this.httpSvc.get(url);
  }

  getSubProcessInstance(superActInstId: string, superProcInstId: string): Observable<any> {
    if (superActInstId) {
      const url = `/api/runtime/wf/v1.0/processInstances/subProcInst?superActInstId=${superActInstId}&superProcInstId=${superProcInstId}`;
      return this.httpSvc.get(url);
    }
  }

  /**
   * 获取所有包含预解析的流程实例信息
   */
  getForecastProcessByProcInstId(procInstId: string): Observable<any> {
    const url = `/api/runtime/wf/v1.0/processInstances/${procInstId}/forecastProcess`;
    return this.httpSvc.get(url);
  }

  /**
   * 获取流程预测数据
   */
  getForecastProcessByPayload(payload: any) {
    const url = '/api/runtime/wf/v1.0/processInstances/forecastProcess';
    return this.httpSvc.post(url, payload);
  }

  getCompleteProcessInfoByProcInstId(procInstId: string): Observable<any> {
    const url = `/api/runtime/wf/v1.0/processInstances/${procInstId}/flowChartInfo`;
    return this.httpSvc.get(url);
  }

  /**
   * 基于流程实例Id获取所有活动实例
   * @param procInstId procInstId
   */
  getAllActiInstsbyProcInstId(procInstId: string): Observable<any> {
    const url = `/api/runtime/wf/v1.0/processInstances/${procInstId}/activityInstanceSlims`;
    return this.httpSvc.get(url);
  }

  /**
   * 基于流程实例ID获取所有的流转实例
   * @param procInstId procInstId
   */
  getTransitionInstanceSlimsByProcInstId(procInstId: string): Observable<any> {
    const url = `/api/runtime/wf/v1.0/processInstances/${procInstId}/transitionInstanceSlims`;
    return this.httpSvc.get(url);
  }

  getFormInfoByProcInstId(procInstId: string): Observable<any> {
    const url = `/api/runtime/wf/v1.0/processInstances/${procInstId}/formInfo`;
    return this.httpSvc.get(url);
  }

  getFormInfoByActInstId(procInstId: string, actInstId: string): Observable<any> {
    let url = `/api/runtime/wf/v1.0/processInstances/${procInstId}/formInfo`;
    if (actInstId) {
      url += `?actInstId=${actInstId}`;
    }
    return this.httpSvc.get(url);
  }

  /**
   * 获取审批日志信息
   * @param ProcInstId ProcInstId
   * 新改造的接口
   */
  getWorkItemLogs(procInstId: string, activityDefinitionId?: string): Observable<any> {
    const logUrl = activityDefinitionId ?
      `/api/runtime/wf/v1.0/processInstances/${procInstId}/logs?activityDefinitionId=${activityDefinitionId}` :
      `/api/runtime/wf/v1.0/processInstances/${procInstId}/logs`;
    return this.httpSvc.get(logUrl);
  }

  getI18nValue(name: string) {
    if (!name) { return ''; }
    const defaultLang = localStorage.getItem('languageCode');
    const langData = defaultLang ? translate[defaultLang] : translate['zh-CHS'];
    let resultVal = '';
    if (name.indexOf('.') === -1) {
      resultVal = langData[name];
    } else {
      resultVal = name.split('.').reduce((obj, key) => {
        if (obj) {
          return obj[key];
        } else {
          return null;
        }
      }, langData);
    }
    return resultVal;
  }

  getProcessInstanceById(processId: string): Observable<any> {
    const url = `/api/runtime/wf/v1.0/processInstances/${processId}`;
    return this.httpSvc.get(url);
  }

  getProcInstIdByDataId(dataId: string): Observable<any> {
    const url = `/api/runtime/wf/v1.0/processInstances/procInstId?bizInstId=${dataId}`;
    return this.httpSvc.get(url);
  }

  retryAifCreation(procInstId: string, actInstId: string, bizInstId: string) {
    return this.httpSvc.post("/api/runtime/wf/v1.0/aifactivityinstance/retry", actInstId);
  }

  /**
   * 是否是外部流程第三方的任务
   * @param processInstanceId 流程实例ID
   */
  ifThirdTask(processInstanceId: string): Observable<any> {
    const url = `/api/runtime/task/v1.0/instances/wfExternalProcess?processInstanceId=${processInstanceId}`;
    return this.httpSvc.get(url);
  }

  /**
   * 获取查看共享流程图的相关参数
   * @param bizId 单据内码
   */
  getFsParamsByBizId(bizId: string): Observable<any> {
    const url = `/api/runtime/task/v1.0/tasks/third/bizId?bizId=${bizId}`;
    return this.httpSvc.get(url);
  }

  /**
   * 获取共享查看流程图的URL
   * @param params 参数
   */
  viewFsProcessNew(params: any): Observable<any> {
    const url = `/api/fssp/ssp/v1.0/mybill/fswybz/viewprocess`;
    return this.httpSvc.post(url, params);
  }


  getFlowChartStyleConfig(): Observable<any> {
    const url = `/platform/runtime/common/web/@gsp-wf/wf-process-editor/config/cell-style-config.json`;
    return this.httpSvc.get(url);
  }

  /**
   * 获取多实例子流程
   * @param actInst 活动实例
   * @param superProcInstId 父流程实例id
   * @returns 
   */
  getMutiChildInstance(actInst: ActivityInstanceSlim, superProcInstId: string): Observable<any> {
    const actInstId = actInst.activityInstanceId;
    const isMultiInstanceRoot = actInst.multiInstanceRoot || false; 
    let url = `/api/runtime/wf/v1.0/processInstances/subProcInstList?actInstId=${actInstId}&isMultiInstanceRoot=${isMultiInstanceRoot}&superProcInstId=${superProcInstId}`;
    return this.httpSvc.get(url);
  }

}
